(() => {
  // Cart gate reconciliation: installment fee + partner welcome pack / starter bundle orphans.

  let cartGateReconcileChain = Promise.resolve();
  let cartGateCheckoutBypass = false;

  const fetchCartGateJson = async () => {
    // Checkout-gate reconciliation reads current server state between mutations,
    // so it always forces a fresh read.
    if (window.JPCartService) {
      return window.JPCartService.getCart({ force: true });
    }

    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  };

  const changeCartGateLineQuantity = async (lineKey, quantity) => {
    if (!lineKey) {
      return false;
    }

    if (window.JPCartService) {
      try {
        // The service waits on the shared-cart restore gate itself.
        await window.JPCartService.change({ id: lineKey, quantity });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const response = await fetch(`${KROWN.settings.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity
      })
    }).catch((error) => {
      console.error(error);
      return null;
    });

    return !!(response && response.ok);
  };

  const setCartReconcileLoading = (isLoading, options = {}) => {
    const isDrawerContext = options.context === 'drawer';
    const cartFormEl = document.getElementById('cart');

    cartFormEl?.classList.toggle('processing', isLoading);

    if (isDrawerContext) {
      document.querySelector('#site-cart-sidebar .sidebar__footer')?.classList.toggle('processing', isLoading);
    }
  };

  const refreshCartAfterReconcile = async () => {
    const response = await fetch('?section_id=helper-cart').catch(() => null);
    if (!response || !response.ok) {
      return;
    }

    const text = await response.text();
    const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
    const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm')?.innerHTML;
    const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal')?.innerHTML;

    const cartItems = document.getElementById('AjaxCartForm');
    if (cartItems && cartFormInnerHTML) {
      cartItems.innerHTML = cartFormInnerHTML;
      if (typeof cartItems.ajaxifyCartItems === 'function') {
        cartItems.ajaxifyCartItems();
      }
      if (typeof cartItems.initializeSubscriptionDetails === 'function') {
        cartItems.initializeSubscriptionDetails();
      }
    }

    document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
      const countEl = cartItems?.querySelector('[data-cart-count]');
      if (countEl) {
        elm.textContent = countEl.textContent;
      }
    });

    document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
      const totalEl = cartItems?.querySelector('[data-cart-total]');
      if (totalEl) {
        elm.textContent = totalEl.textContent;
      }
    });

    const ajaxCartSubtotal = document.getElementById('AjaxCartSubtotal');
    if (ajaxCartSubtotal && cartSubtotalInnerHTML) {
      ajaxCartSubtotal.innerHTML = cartSubtotalInnerHTML;
    }

    const cartForm = document.querySelector('cart-form');
    if (cartForm) {
      cartForm.dispatchEvent(new Event('cart-updated', { bubbles: true }));
    }

    if (typeof window.refreshCartPaymentMethods === 'function') {
      await new Promise((resolve) => {
        setTimeout(async () => {
          await window.refreshCartPaymentMethods();
          resolve();
        }, 100);
      });
    }
  };

  const runSerializedCartGateReconcile = (task) => {
    const run = cartGateReconcileChain.then(task, task);
    cartGateReconcileChain = run.catch(() => {});
    return run;
  };

  // #region installment fee reconcile

  const INSTALLMENT_FEE_HSF_COUNTRIES = ['US', 'CA', 'MX', 'AU', 'NZ'];

  const getInstallmentFeeVariantId = () => {
    const variantId = parseInt(localStorage.getItem('installmentFeeVariantId') || '', 10);
    return Number.isFinite(variantId) ? variantId : null;
  };

  const hasInstallmentFeeStoreConfig = () => {
    const variantId = parseInt(localStorage.getItem('installmentFeeVariantId') || '', 10);
    const sellingPlanId = parseInt(localStorage.getItem('installmentFeeVariantSellingPlanId') || '', 10);
    return Number.isFinite(variantId) && Number.isFinite(sellingPlanId);
  };

  const shouldValidateHSFForInstallmentFee = () => {
    const country = typeof Shopify !== 'undefined' ? Shopify.country : '';
    return !INSTALLMENT_FEE_HSF_COUNTRIES.includes(country);
  };

  const getInstallmentProductsThatApplyFee = (cartItems) => {
    const feeProductId = Number(localStorage.getItem('installmentFeeProductId'));
    const feeVariantId = getInstallmentFeeVariantId();

    return (cartItems || []).filter((item) => {
      const variantId = item.variant_id ?? item.id;
      if (feeVariantId && variantId === feeVariantId) {
        return false;
      }
      if (feeProductId && item.product_id === feeProductId) {
        return false;
      }
      if (item.id === feeProductId) {
        return false;
      }
      if (item.properties?._subscriptionType !== 'installment') {
        return false;
      }
      if (shouldValidateHSFForInstallmentFee() && item.properties?._hsf) {
        return false;
      }
      return true;
    });
  };

  const getInstallmentFeeLines = (cartItems) => {
    const feeVariantId = getInstallmentFeeVariantId();
    if (!feeVariantId) {
      return [];
    }

    return (cartItems || []).filter((item) => {
      const variantId = item.variant_id ?? item.id;
      return variantId === feeVariantId;
    });
  };

  const reconcileInstallmentFeeCart = async (cart) => {
    let changed = false;
    const installmentProducts = getInstallmentProductsThatApplyFee(cart.items);
    let feeLines = getInstallmentFeeLines(cart.items);

    if (installmentProducts.length === 0) {
      if (feeLines.length > 0) {
        for (const line of feeLines) {
          if (await changeCartGateLineQuantity(line.key, 0)) {
            changed = true;
          }
        }
      }
      return { changed, cart };
    }

    if (!localStorage.getItem('installmentFeeVariantId') || !localStorage.getItem('installmentFeeVariantSellingPlanId')) {
      return { changed: false, cart };
    }

    if (feeLines.length > 1) {
      for (let i = 1; i < feeLines.length; i++) {
        if (await changeCartGateLineQuantity(feeLines[i].key, 0)) {
          changed = true;
        }
      }
      cart = await fetchCartGateJson();
      feeLines = getInstallmentFeeLines(cart.items);
    }

    if (feeLines.length === 1 && feeLines[0].quantity > 1) {
      if (await changeCartGateLineQuantity(feeLines[0].key, 1)) {
        changed = true;
      }
      cart = await fetchCartGateJson();
      feeLines = getInstallmentFeeLines(cart.items);
    }

    if (feeLines.length === 0 && typeof window.postInstallmentFeeItem === 'function') {
      const isManualPayment = typeof window.resolveInstallmentFeeManualPayment === 'function'
        ? window.resolveInstallmentFeeManualPayment(cart)
        : false;
      if (await window.postInstallmentFeeItem(isManualPayment)) {
        changed = true;
        cart = await fetchCartGateJson();
      }
    }

    return { changed, cart };
  };

  // #endregion

  // #region partner fee / welcome pack / starter bundle reconcile

  const cartHasPartnerRelatedSignals = (cart) => {
    if (cart?.attributes?._partnerFeeProduct) {
      return true;
    }

    return (cart?.items || []).some((item) => {
      const props = item.properties || {};
      if (props._partner_fee === 'true') {
        return true;
      }
      if (props._partner_fee_welcome_pack === 'true') {
        return true;
      }
      return String(props._partner_fee_bundle || '').trim() !== '';
    });
  };

  const getPartnerFeeLines = (cartItems) => (
    (cartItems || []).filter((item) => item.properties?._partner_fee === 'true')
  );

  const getPartnerFeeVariantIds = (cartItems) => (
    getPartnerFeeLines(cartItems).map((item) => String(item.variant_id ?? item.id))
  );

  const isPartnerWelcomePackLine = (item) => (
    item.properties?._partner_fee_welcome_pack === 'true' &&
    String(item.properties?._partner_fee_bundle || '').trim() !== ''
  );

  const isPartnerStarterBundleLine = (item) => {
    const props = item.properties || {};
    return (
      String(props._partner_fee_bundle || '').trim() !== '' &&
      props._partner_fee_welcome_pack !== 'true' &&
      props._partner_fee !== 'true'
    );
  };

  const isOrphanPartnerRelatedLine = (item, partnerFeeVariantIds) => {
    if (!isPartnerWelcomePackLine(item) && !isPartnerStarterBundleLine(item)) {
      return false;
    }

    const bundleVariantId = String(item.properties?._partner_fee_bundle || '').trim();
    if (!bundleVariantId) {
      return false;
    }

    if (partnerFeeVariantIds.length === 0) {
      return true;
    }

    return !partnerFeeVariantIds.includes(bundleVariantId);
  };

  const clearStalePartnerFeeAttribute = async (cart) => {
    if (getPartnerFeeLines(cart.items).length > 0) {
      return false;
    }

    if (!cart.attributes?._partnerFeeProduct) {
      return false;
    }

    if (window.JPCartService) {
      try {
        // The service waits on the shared-cart restore gate itself.
        await window.JPCartService.update({ attributes: { _partnerFeeProduct: null } });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const response = await fetch(`${KROWN.settings.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _partnerFeeProduct: null
        }
      })
    }).catch((error) => {
      console.error(error);
      return null;
    });

    return !!(response && response.ok);
  };

  const reconcilePartnerRelatedCart = async (cart) => {
    let changed = false;
    const partnerFeeVariantIds = getPartnerFeeVariantIds(cart.items);

    for (const line of cart.items || []) {
      if (!isOrphanPartnerRelatedLine(line, partnerFeeVariantIds)) {
        continue;
      }
      if (await changeCartGateLineQuantity(line.key, 0)) {
        changed = true;
      }
    }

    if (changed) {
      cart = await fetchCartGateJson();
    }

    if (await clearStalePartnerFeeAttribute(cart)) {
      changed = true;
      cart = await fetchCartGateJson();
    }

    return { changed, cart };
  };

  // #endregion

  const reconcileCartAtGate = async (cart) => {
    let changed = false;

    if (hasInstallmentFeeStoreConfig()) {
      const installmentResult = await reconcileInstallmentFeeCart(cart);
      changed = changed || installmentResult.changed;
      cart = installmentResult.cart;

      if (installmentResult.changed) {
        cart = await fetchCartGateJson();
      }
    }

    if (cartHasPartnerRelatedSignals(cart)) {
      const partnerResult = await reconcilePartnerRelatedCart(cart);
      changed = changed || partnerResult.changed;
      cart = partnerResult.cart;
    }

    return { changed, cart };
  };

  const runCartGateReconcile = (options = {}) => {
    const shouldRefresh = options.refresh !== false;
    const loadingContext = options.context || 'default';

    return runSerializedCartGateReconcile(async () => {
      if (typeof window.awaitSharedCartRestore === 'function') {
        await window.awaitSharedCartRestore();
      }

      setCartReconcileLoading(true, { context: loadingContext });
      try {
        let cart = await fetchCartGateJson();
        const result = await reconcileCartAtGate(cart);
        if (result.changed && shouldRefresh) {
          await refreshCartAfterReconcile();
        }
        return result.changed;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        setCartReconcileLoading(false, { context: loadingContext });
      }
    });
  };

  const hookCartDrawerShow = (drawer) => {
    if (!drawer || drawer.dataset.cartGateReconcileHooked === 'true') {
      return false;
    }

    if (typeof drawer.show !== 'function') {
      return false;
    }

    drawer.dataset.cartGateReconcileHooked = 'true';
    const originalShow = drawer.show.bind(drawer);

    drawer.show = function showCartDrawerWithReconcile(...args) {
      originalShow(...args);
      runCartGateReconcile({ refresh: true, context: 'drawer' });
    };

    return true;
  };

  const initCartDrawerHook = () => {
    const drawer = document.getElementById('site-cart-sidebar');
    if (!drawer) {
      return;
    }

    if (hookCartDrawerShow(drawer)) {
      return;
    }

    if (typeof customElements?.whenDefined === 'function') {
      customElements.whenDefined('sidebar-drawer').then(() => {
        hookCartDrawerShow(document.getElementById('site-cart-sidebar'));
      }).catch(() => {});
    }
  };

  const initCheckoutHook = () => {
    const cartForm = document.getElementById('cart');
    if (!cartForm || cartForm.dataset.cartGateReconcileHooked === 'true') {
      return;
    }

    cartForm.dataset.cartGateReconcileHooked = 'true';

    cartForm.addEventListener('submit', async (event) => {
      if (cartGateCheckoutBypass) {
        return;
      }

      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || submitter.disabled) {
        return;
      }

      if (submitter.classList.contains('disabled')) {
        return;
      }

      event.preventDefault();

      const loadingContext = document.getElementById('site-cart-sidebar')?.classList.contains('sidebar--opened')
        ? 'drawer'
        : 'default';

      await runCartGateReconcile({ refresh: true, context: loadingContext });

      cartGateCheckoutBypass = true;
      cartForm.requestSubmit(submitter);
    });
  };

  const initCartGateHooks = () => {
    initCartDrawerHook();
    initCheckoutHook();

    if (document.body.classList.contains('template-cart')) {
      runCartGateReconcile({ refresh: true, context: 'default' });
    }
  };

  const scheduleCartGateHooks = () => {
    if (document.readyState === 'complete') {
      initCartGateHooks();
      return;
    }

    document.addEventListener('DOMContentLoaded', initCartGateHooks, { once: true });
  };

  if (typeof window !== 'undefined') {
    window.reconcileCartAtGate = runCartGateReconcile;
    window.reconcileInstallmentFeeAtCartGate = runCartGateReconcile;
    window.refreshCartPageSection = refreshCartAfterReconcile;
  }

  scheduleCartGateHooks();
})();
